import {
  buildUserAddress,
  transformOrderTotalsDetails,
  generateBasicName,
  sanitizePhoneNumber,
} from './orderDetail.utils';

import { SUBSTITUTION_FLOW_STATUS_MAP } from './orderDetail.const';

import type {
  OrderDetailModel,
  SubstitutionFlowState,
  ToppingInOrderDetail,
} from 'shared/domain';
import type { OrderDetailRawResp } from 'shared/infra';

export function toOrderDetailModel(data: OrderDetailRawResp): OrderDetailModel {
  type SFpr = SubstitutionFlowState['products'][0]['replacements'][0];
  type SFcr = SubstitutionFlowState['toppings'][0]['replacements'][0];
  return {
    id: data.id,
    status: data.partner_order_state,
    statusAuxiliary: data.state,
    bundleId: data.bundle_id,
    isEnabledOM: data.is_enabled ?? false,
    isAuditable: data.is_auditable ?? false,
    specialOrderTypes: data.special_order ?? [],
    deliveryMethod: data.delivery_method ?? 'delivery',
    products: data.products.map(p => ({
      id: p.id,
      orderProductId: p.order_product_id,
      name: p.name,
      image: p.image ?? '',
      description: p.description ?? '',
      units: p.units ?? 0,
      comments: p.comments ?? '',
      storeId: p.store_id,
      price: {
        unit: p.unit_price,
        unitReal: p.unit_real_price,
        total: p.total_price_product,
        totalReal: p.total_real_price,
        totalToppings: p.total_price_toppings ?? 0,
      },
      toppings: p.toppings
        .map(
          t =>
            ({
              id: t.id,
              index: t.topping_category_index,
              toppingNodeId: t.order_product_topping_id ?? NaN,
              parentID: t.parent_id ?? NaN,
              name: t.topping_category.toUpperCase(),
              description: t.description ?? '',
              units: t.units ?? 0,
              price: {
                base: t.price,
                total: t.total_price,
                real: t.total_real_price,
              },
              children: [],
            } as ToppingInOrderDetail),
        )
        .sort((a, b) => a.index - b.index),
    })),
    substitutionFlowState:
      data.substitution_flow_state === null
        ? null
        : {
            orderId: data.substitution_flow_state.order_id,
            storeId: data.substitution_flow_state.store_id,
            partnerId: data.substitution_flow_state.partner_id,
            flowType: data.substitution_flow_state.flow_type,
            status:
              SUBSTITUTION_FLOW_STATUS_MAP[
                data.substitution_flow_state.status
              ] ?? 'awaiting',
            createdAt: new Date(
              data.substitution_flow_state.created_at ?? Date.now(),
            ),
            deletedAt: new Date(
              data.substitution_flow_state.deleted_at ?? Date.now(),
            ),
            products: data.substitution_flow_state.product_substitution_details.map(
              p => ({
                productId: p.replaced_details.product_id,
                orderProductId: p.replaced_details.order_product_id,
                name: p.replaced_details.product_name,
                description: p.replaced_details.product_description ?? '',
                image: p.replaced_details.image ?? '',
                units: p.replaced_details.units ?? 0,
                replacements: p.replacement_details.map<SFpr>(r => ({
                  orderProductId: r.order_product_id,
                  productId: r.product_id,
                  name: r.product_name,
                  description: r.product_description ?? '',
                  image: r.image ?? '',
                  units: r.units ?? 0,
                })),
              }),
            ),
            toppings: data.substitution_flow_state.topping_substitution_details.map(
              c => ({
                productId: c.replaced_details.product_id,
                orderProductId: c.replaced_details.order_product_id,
                productName: c.replaced_details.product_name,
                id: c.replaced_details.topping_id,
                name: c.replaced_details.topping_description ?? '',
                customizationCategoryId:
                  c.replaced_details.topping_category_id ?? 0,
                image: c.replaced_details.image ?? '',
                units: c.replaced_details.units ?? 0,
                replacements: c.replacement_details.map<SFcr>(cr => ({
                  productId: cr.product_id,
                  orderProductId: cr.order_product_id,
                  productName: cr.product_name,
                  id: cr.topping_id,
                  name: cr.topping_description ?? '',
                  customizationCategoryId: cr?.topping_category_id ?? 0,
                  image: cr.image ?? '',
                  units: cr.units ?? 0,
                })),
              }),
            ),
          },
    user: {
      id: data.user.id,
      name: {
        first: data.user.first_name ?? '',
        last: data.user.last_name ?? '',
        full: `${data.user.first_name ?? ''} ${
          data.user.last_name ?? ''
        }`.trim(),
        basic: generateBasicName(data.user),
      },
      tag: {
        isNew: data.user.is_new_user ?? false,
        isPro: data.user.high_value_user ?? false,
      },
      chat: {
        isUserAppEnabled: data.user_app_chat_enabled ?? false,
      },
      phone: {
        hasProxySession: data.proxy_session ?? false,
        number: sanitizePhoneNumber(data.user.phone),
      },
      address: buildUserAddress(data.user),
      billing: {
        hasDocument: data.user.has_document_type ?? false,
        type: data.user.bill_type ?? '',
        email: data.user.billing_email ?? '',
        address: data.user.billing_address ?? '',
        phone: data.user.billing_phone ?? '',
        document: {
          owner: {
            label: data.user.document_type_owner_label ?? '',
            value: data.user.document_type_owner_value ?? '',
          },
          description: {
            label: data.user.document_type_description_label ?? '',
            value: data.user.document_type_description_value ?? '',
          },
        },
      },
    },
    totals: {
      main: {
        id: data.order_totals.total.id,
        label: data.order_totals.total.text ?? '',
        price: data.order_totals.total.value ?? 0.0,
        isZero: data.order_totals.total.value === 0,
        isNegative: data.order_totals.total.value < 0,
      },
      detail: transformOrderTotalsDetails(data.order_totals.details),
      error: data.order_totals.errors,
    },
  };
}

export const orderDetailAdapter = {
  toOrderDetailModel,
};
